import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import Modal from './Modal/Modal'
import { formatDateToMMDDYYYY, formatUTCTo12Hour } from '../../utils/date'
import { getTimeOffTypeLabel } from '../../utils/timeOffTypes'

interface TooltipBannerProps {
  header?: React.ReactNode
  message?: React.ReactNode
  width?: string
  padding?: string
  children?: React.ReactNode
  showOnHover?: boolean
  /** When true, the trigger (children) toggles the tooltip on click instead of hover. */
  triggerOnClick?: boolean
  tooltipContainerClass?: string
  className?: string
  onClose?: () => void
  /** When provided, a "See more" button is rendered at the bottom of the tooltip. Clicking it opens a modal with the full details. */
  onSeeMore?: () => void
  /** Title shown in the "See more" modal. Defaults to "Details". */
  modalTitle?: string
  /** Called with `true` when the detail modal opens and `false` when it closes.
   * Use this to prevent the parent from unmounting this component while the modal is open. */
  onModalStateChange?: (isOpen: boolean) => void
  /** Array of individual requests to display in the modal (for partial day requests). */
  individualRequests?: Array<{
    startTime?: string
    endTime?: string
    allocations: Array<{
      typeLabel: string
      hours: number
    }>
    totalHours: number
  }>
}

const TooltipBanner: React.FC<TooltipBannerProps> = ({
  header,
  message,
  width = 'w-full sm:w-[420px]',
  padding = 'p-4!',
  children,
  showOnHover = true,
  triggerOnClick = false,
  tooltipContainerClass = '',
  className = '',
  onSeeMore,
  modalTitle = 'Details',
  onModalStateChange,
  individualRequests,
  onClose,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  // Prevents the tooltip re-appearing while the mouse is still physically
  // over the trigger element after a click (See more, date, etc.).
  const suppressHoverRef = useRef(false)
 const calculateTotalHours = (
    requests: Array<{
      allocations: Array<{
        typeLabel: string
        hours: number
      }>
    }>
  ) => {
    return requests.reduce((total, request) => {
      return (
        total +
        request.allocations.reduce(
          (sum, allocation) => sum + allocation.hours,
          0
        )
      )
    }, 0)
  }

   const totalHours = individualRequests
    ? calculateTotalHours(individualRequests)
    : 0
  // Hide tooltip on any mousedown anywhere — covers date clicks, See more,
  // and any other interaction while the tooltip is visible.
  useEffect(() => {
    if (!isHovered) return
    const hide = () => {
      suppressHoverRef.current = true
      setIsHovered(false)
    }
    globalThis.addEventListener('mousedown', hide)
    return () => globalThis.removeEventListener('mousedown', hide)
  }, [isHovered])

  const openModal = () => {
    setIsModalOpen(true)
    onModalStateChange?.(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    onModalStateChange?.(false)
  }

  /** Opens the detail modal and dismisses the tooltip. */
  const handleSeeMore = () => {
    suppressHoverRef.current = true
    setIsHovered(false)
    openModal()
  }

  const tooltipContent = (
    <aside
      role="status"
      aria-live="polite"
      className={`bg-white opacity-100 shadow-md border border-gray-200 rounded-xl ${padding} ${width} max-w-[calc(100vw-32px)] sm:max-w-full overflow-hidden word-break ${className}`}
    >
      <div className="flex flex-col gap-2">
        {header && (
          <div className="flex items-center justify-between gap-2">
            <h3 className="p3 -medium m-0! line-clamp-2">{header}</h3>
            <button
              type="button"
              aria-label="Close tooltip"
              onClick={() => {
                setIsDismissed(true)
                onClose?.()
              }}
              className="shrink-0 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/images/close-outline.svg"
                alt=""
                aria-hidden="true"
                className="w-5 h-5 shrink-0"
              />
            </button>
          </div>
        )}

        {message && (
          <div
            className={`p4 -book ${header ? 'mt-0.5' : 'mt-0'} line-clamp-4 wrap-break-word`}
          >
            {message}
          </div>
        )}

        {onSeeMore && (
          <button
            type="button"
            onClick={handleSeeMore}
            className="p4 -medium text-[#0078B3] underline text-left hover:text-[#00537D] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078B3] rounded self-start mt-1"
          >
            See more
          </button>
        )}
      </div>
    </aside>
  )

  // Resolve the modal title: use the header value when it is a plain string,
  // otherwise fall back to the modalTitle prop (default: 'Details').
  const resolvedModalTitle =
    typeof header === 'string' && header.trim() ? header.trim() : modalTitle

  // Modal — rendered via a portal so it always mounts at document.body,
  // escaping any parent overflow, transform, or z-index constraints.
  const detailModal =
    isModalOpen && onSeeMore
      ? ReactDOM.createPortal(
          <Modal
            isOpen
            title={resolvedModalTitle}
            onClose={closeModal}
            onSecondary={closeModal}
            clearBtn="Close"
          >
            <div className="flex flex-col gap-6">
              {/* Show all individual requests stacked vertically */}
              {individualRequests && individualRequests.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <div className="p3 -medium mb-2">
                    Partial Day Requests for{' '}
                    {formatDateToMMDDYYYY(
                      individualRequests[0]
                        .startTime!.split('T')[0]
                        .replaceAll('-', '/')
                    )}
                  </div>
                  {individualRequests.map((request, position) => (
                    <div
                      key={`${request.startTime}-${request.endTime}-${position}`}
                      className="pl-3 py-1"
                    >
                      <h4 className="p3 -medium text-gray-800 m-0 mb-1">
                        Request {position + 1}
                      </h4>
                      {/* Start and End Times */}
                      {request.startTime && request.endTime && (
                        <div className="space-y-1 mb-1">
                          <div className="p4 -book text-gray-700">
                            Start Time: {formatUTCTo12Hour(request.startTime)}
                          </div>
                          <div className="p4 -book text-gray-700">
                            End Time: {formatUTCTo12Hour(request.endTime)}
                          </div>
                        </div>
                      )}

                      {/* Allocations */}
                      {request.allocations.length > 0 && (
                        <div className="mb-1">
                          {request.allocations.map((alloc, allocIdx) => {
                            const mappedLabel = getTimeOffTypeLabel(
                              alloc.typeLabel
                            )
                            const suffix =
                              mappedLabel === 'Vacation' ? ' (PTO/ETO)' : ''
                            return (
                              <div
                                key={`${alloc.typeLabel}-${allocIdx}`}
                                className="p4 -book text-gray-700"
                              >
                                {mappedLabel}
                                {suffix} ({alloc.hours.toFixed(2)}{' '}
                                {alloc.hours > 1 ? 'hours' : 'hour'})
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Total Hours */}
                      <div className="p4 -medium ">
                        Total Hours on this date:{' '}
  {totalHours.toFixed(2)}{' '}
  {totalHours > 1 ? 'hours' : 'hour'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Fallback: Show header as a section label only when it differs from the modal title */}
                  {header && typeof header !== 'string' && (
                    <div className="p3 -medium">{header}</div>
                  )}
                  {message && (
                    <div className="p4 -book wrap-break-word leading-relaxed">
                      {message}
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal>,
          document.body
        )
      : null

  // If children provided, use as trigger with hover logic
  if (children && showOnHover) {
    return (
      <>
        {detailModal}
        <div
          role="tooltip"
          onMouseEnter={() => {
            if (!suppressHoverRef.current) setIsHovered(true)
          }}
          onMouseLeave={() => {
            suppressHoverRef.current = false
            setIsHovered(false)
            // Re-arm the close button so the tooltip can reappear on next hover
            setIsDismissed(false)
          }}
          className="relative inline-block"
        >
          {children}
          {isHovered && !isDismissed && (
            <div className={`absolute ${tooltipContainerClass}`}>
              {tooltipContent}
            </div>
          )}
        </div>
      </>
    )
  }

  // Click-to-toggle trigger
  if (children && triggerOnClick) {
    return (
      <>
        {detailModal}
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => {
            // toggle
            setIsHovered((v) => !v)
            // reset dismissed flag when re-opening
            if (!isHovered) setIsDismissed(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsHovered((v) => !v)
              if (!isHovered) setIsDismissed(false)
            }
          }}
          className="relative inline-block bg-transparent border-0 p-0"
        >
          {children}
          {isHovered && !isDismissed && (
            <div className={`absolute ${tooltipContainerClass}`}>
              {tooltipContent}
            </div>
          )}
        </button>
      </>
    )
  }

  // Fallback: render tooltip directly (backward compatible)
  return (
    <>
      {detailModal}
      {tooltipContent}
    </>
  )
}

export default TooltipBanner
